import BaseAPIService from '../base/base-api-service';
import UserModel from 'src/model/user.model';
import env from 'src/shared/env';
import axios from 'axios';
import tokenService from 'src/services/token/TokenService';
import * as normalizeUrl from 'normalize-url';

export interface IChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * This endpoint can receive the next params
 * q: string,
 * size: number,
 * page: number
 */

class UserService extends BaseAPIService<UserModel> {
  public baseURL: string = env.REACT_APP_BASE_API_URL;
  public endpoint: string = '/accountapp/v1';

  constructor() {
    super(UserModel);
  }

  public castTokenToClass(tokenData: any): any {
    const newUser = new UserModel();
    newUser.sub = tokenData.sub;
    newUser.accountId = tokenData.accountId;
    newUser.specialUser = tokenData.specialUser;
    newUser.permission = tokenData.scopes;
    newUser.tokenEmittedIn = tokenData.iat;
    newUser.tokenExpiration = tokenData.exp;
    newUser.specialUserEmail = tokenData.specialUserEmail;
    return newUser;
  }

  public getUserDetails(): Promise<UserModel> {
    return this.getOneByParams(`${this.endpoint}/webuser/client`, {}, {});
  }

  public changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<boolean> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'PATCH',
      data: {
        currentPassword,
        newPassword
      },
      url: `${this.endpoint}/webuser/change/password`
    }).then(res => {
      console.log(res);
      return true;
    });
  }

  public forgotPassword(mailPhone: string): Promise<any> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'GET',
      params: {
        mailPhone
      },
      url: `${this.endpoint}/webuser/forgot/password`
    })
      .then(res => {
        return res.data.associatedEmailId;
      })
      .catch(error => {
        throw error;
      });
  }

  public forgotUserId(mailPhone: string): Promise<boolean> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'GET',
      params: {
        mailPhone
      },
      url: `${this.endpoint}/webuser/forgot/userid`
    }).then(res => {
      return true;
    });
  }

  public async unlockUser(clientIdentity: string): Promise<any> {
    const headers = await tokenService
      .getAccessToken()
      .then(token => {
        if (token !== null) {
          // Only add token if it is valid
          return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          };
        } else {
          return {
            'Content-Type': 'application/json'
          };
        }
      })
      .catch(error => {
        throw error;
      });
    return axios
      .patch(
        normalizeUrl(
          `${this.baseURL}${this.endpoint}/webuser/activate/${clientIdentity}`
        ),
        clientIdentity,
        {
          headers
        }
      )
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        return error.response;
      });
  }

  public verifyCode(emailPhone: string, code: string): Promise<boolean> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'GET',
      params: {
        emailPhone
      },
      url: `${this.endpoint}/security/api/auth/2fa/${code}`
    })
      .then(res => {
        return res.data;
      })
      .catch(err => {
        return false;
      });
  }

  public forgotPasswordReset(
    emailPhone: string,
    code: string,
    newPassword: string
  ): Promise<boolean> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'PATCH',
      data: {
        emailPhone,
        newPassword,
        code
      },
      url: `${this.endpoint}/webuser/change/forgot/password`
    }).then(res => {
      return true;
    });
  }

  public updateUserInfo(email: string, phone: string): Promise<boolean> {
    return this.doRequest({
      baseURL: this.baseURL,
      method: 'PATCH',
      data: {
        email,
        phone
      },
      url: `${this.endpoint}/webuser/client`
    })
      .then(res => {
        return res.data;
      })
      .catch(error => {
        throw error;
      });
  }
}

const userServiceInstance = new UserService();
Object.freeze(userServiceInstance);

export default userServiceInstance;
